<!-- eslint-disable vue/no-v-html -->
<template>
    <div class="custom-modal" @focusin="onFocus" @focusout="onFocusout" @click.prevent>
        <div class="custom-modal__wrapper">
            <div class="custom-modal__title">{{ props.title }}</div>
            <div class="custom-modal__description" v-html="message" />
            <div class="custom-modal__button-container">
                <button
                    v-for="(btn, index) in props.buttons"
                    :id="getId(index)"
                    ref="btns"
                    :key="getButtonValue(btn)"
                    class="custom-modal__btn btn"
                    :data-keyright="getRightButton(index)"
                    :data-keyleft="getLeftButton(index)"
                    data-type="modal-button"
                    @click="onClick(getButtonValue(btn))"
                >
                    {{ getButtonValue(btn) }}
                </button>
                
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Strings } from "@/locales/types/enums/strings";
    import { useOnline } from '@vueuse/core'
    import { useI18n } from "vue-i18n";
    import { onMounted, onBeforeUnmount, type Ref, ref, type PropType, isRef } from "vue";

    const props = defineProps({
        title: {
            type: [String, null] as PropType<ComputedRef<string> | string | null>,
            required: true
        },
        message: {
            type: [String, null] as PropType<ComputedRef<string> | string | null>,
            required: false,
            default: ""
        },
        buttons: {
            type: [Array, null] as PropType<string[] | Array<ComputedRef<string>> | null>,
            required: true
        },
        name: {
            type: [String, null] as PropType<string | null>,
            required: true
        },
        focusedInd: {
            type: Number,
            required: false,
            default: 0
        }
    });

    const emit = defineEmits(['on-btn-click'])
    const { t } = useI18n();
    const { $navigator } = useNuxtApp();
    const closeModalText: string = t(Strings.SETTINGS_ACCOUNT_MODAL_NEGATIVE).toString();
    const btns: Ref<HTMLButtonElement[]> = ref([]);
    const focusedInd: Ref<number> = ref(props.focusedInd ?? 0);
    const isRegKeyListeners: Ref<boolean> = ref(false);

    const getButtonValue = (btn: string | ComputedRef<string>): string => {
        if (isRef(btn)) {
            return btn.value;
        }
        return btn;
    };
    
    const getId = (index: number): string => {
        return `btn-${props.name}-${index}`;
    };
    const getRightButton = (index: number): string => {
        const nextInd = index + 1;
        if (props.buttons && (nextInd < props.buttons.length)) {
            return getId(nextInd);
        }
        return getId(index);
    };
    const getLeftButton = (index: number): string => {
        const nextInd = index - 1;
        if (nextInd >= 0) {
            return getId(nextInd);
        }
        return getId(index);
    };
    const onClick = (item: string) => {
        emit("on-btn-click", item);
    };
    const onFocus = () => {
        registerKeyListeners();
    };
    const onFocusout = () => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = () => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:back", closeModal);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = () => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:back", closeModal);
            isRegKeyListeners.value = false;
        }
    };
    const closeModal = () => {
        if (!useOnline().value) {
            return;
        }
        onClick(closeModalText);
    }

    onMounted(() => { 
        $navigator.disableDownButton();
        btns.value[focusedInd.value].focus();
    });
    onBeforeUnmount(() => {
        $navigator.enableDownButton();
    });
</script>